import { useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { ErrorMessage } from '@/components/common/ErrorMessage'
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts'
import { ArrowLeft } from 'lucide-react'
import { subDays, subHours } from 'date-fns'
import { useDevice } from '@/hooks/useDevices'
import { useChartData } from '@/hooks/useMeasurements'
import { formatChartTimestamp } from '@/utils/date'
import type { DeviceType } from '@/types/device.types'

type TimePeriod = '24h' | '7d' | '30d' | '90d'

interface ParameterConfig {
  value: string
  label: string
  unit: string
  color: string
  chartType: 'area' | 'line'
}

const PARAMETER_MAP: Record<DeviceType, ParameterConfig[]> = {
  watersense: [
    { value: 'liquid_level', label: 'Water Level', unit: 'mm', color: '#3b82f6', chartType: 'area' },
    { value: 'temperature', label: 'Temperature', unit: '\u00b0C', color: '#f59e0b', chartType: 'line' },
    { value: 'humidity', label: 'Humidity', unit: '%', color: '#06b6d4', chartType: 'line' },
    { value: 'rain', label: 'Rainfall', unit: 'mm', color: '#2563eb', chartType: 'line' },
    { value: 'battery_percentage', label: 'Battery', unit: '%', color: '#10b981', chartType: 'line' },
    { value: 'solar_voltage', label: 'Solar Voltage', unit: 'mV', color: '#eab308', chartType: 'line' },
  ],
  rk900: [
    { value: 'temperature', label: 'Temperature', unit: '\u00b0C', color: '#f59e0b', chartType: 'line' },
    { value: 'humidity', label: 'Humidity', unit: '%', color: '#06b6d4', chartType: 'line' },
    { value: 'pressure', label: 'Pressure', unit: 'hPa', color: '#8b5cf6', chartType: 'line' },
    { value: 'wind_speed', label: 'Wind Speed', unit: 'm/s', color: '#64748b', chartType: 'line' },
    { value: 'rainfall_mm', label: 'Rainfall', unit: 'mm', color: '#2563eb', chartType: 'line' },
    { value: 'sm1_moisture', label: 'Soil Moisture', unit: '%', color: '#0891b2', chartType: 'area' },
    { value: 'battery_percentage', label: 'Battery', unit: '%', color: '#10b981', chartType: 'line' },
    { value: 'air_quality_pm2p5', label: 'PM2.5', unit: '\u00b5g/m\u00b3', color: '#ef4444', chartType: 'line' },
  ],
  dht11soil: [
    { value: 'soilmoisture_level', label: 'Soil Moisture', unit: '%', color: '#0891b2', chartType: 'area' },
    { value: 'temperature', label: 'Temperature', unit: '\u00b0C', color: '#f59e0b', chartType: 'line' },
    { value: 'humidity', label: 'Humidity', unit: '%', color: '#06b6d4', chartType: 'line' },
    { value: 'battery_percentage', label: 'Battery', unit: '%', color: '#10b981', chartType: 'line' },
  ],
}

const TIME_PERIODS: { value: TimePeriod; label: string }[] = [
  { value: '24h', label: '24 Hours' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
]

function getDateRange(period: TimePeriod): { startDate: string; endDate: string } {
  const endDate = new Date()
  let startDate: Date

  switch (period) {
    case '24h':
      startDate = subHours(endDate, 24)
      break
    case '7d':
      startDate = subDays(endDate, 7)
      break
    case '30d':
      startDate = subDays(endDate, 30)
      break
    case '90d':
      startDate = subDays(endDate, 90)
      break
    default:
      startDate = subDays(endDate, 7)
  }

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  }
}

export const DeviceHistoryPage = () => {
  const { deviceId } = useParams<{ deviceId: string }>()
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('7d')

  const { data: device, isLoading: deviceLoading, error: deviceError } = useDevice(deviceId!)

  const deviceType: DeviceType = (device?.deviceType as DeviceType) || 'watersense'
  const parameters = PARAMETER_MAP[deviceType] || PARAMETER_MAP.watersense
  const [selectedParameter, setSelectedParameter] = useState<string>(parameters[0].value)

  // Reset parameter when device type changes and current selection is invalid
  const currentConfig = useMemo(() => {
    const params = PARAMETER_MAP[deviceType] || PARAMETER_MAP.watersense
    const found = params.find(p => p.value === selectedParameter)
    return found || params[0]
  }, [deviceType, selectedParameter])

  const dateRange = useMemo(() => getDateRange(timePeriod), [timePeriod])

  const {
    data: chartData,
    isLoading: chartLoading,
    error: chartError,
  } = useChartData(deviceId!, currentConfig.value, dateRange)

  const formattedChartData = useMemo(() => {
    if (!chartData) return []
    return chartData.map((point) => ({
      timestamp: formatChartTimestamp(point.timestamp),
      value: point.value,
    }))
  }, [chartData])

  if (deviceLoading) {
    return <LoadingSpinner />
  }

  if (deviceError || !device) {
    return <ErrorMessage message="Failed to load device details. Please try again." />
  }

  const availableParameters = PARAMETER_MAP[deviceType] || PARAMETER_MAP.watersense

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" asChild>
              <Link to={`/devices/${deviceId}`}>
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold">{device.name}</h1>
          </div>
          <p className="text-sm text-muted-foreground ml-10 sm:ml-12">
            Full Measurement History
          </p>
        </div>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="flex flex-col sm:flex-row items-start sm:items-center gap-4 py-4">
          {/* Time period buttons */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">Period:</span>
            <div className="flex gap-1">
              {TIME_PERIODS.map((period) => (
                <Button
                  key={period.value}
                  variant={timePeriod === period.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTimePeriod(period.value)}
                >
                  {period.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Parameter selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">Parameter:</span>
            <Select value={currentConfig.value} onValueChange={setSelectedParameter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select parameter" />
              </SelectTrigger>
              <SelectContent>
                {availableParameters.map((param) => (
                  <SelectItem key={param.value} value={param.value}>
                    {param.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Chart */}
      <Card>
        <CardHeader>
          <CardTitle>{currentConfig.label} History</CardTitle>
          <CardDescription>
            {currentConfig.label} ({currentConfig.unit}) over the last {TIME_PERIODS.find(p => p.value === timePeriod)?.label?.toLowerCase()}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {chartLoading ? (
            <div className="flex items-center justify-center h-[500px]">
              <LoadingSpinner />
            </div>
          ) : chartError ? (
            <div className="flex items-center justify-center h-[500px] text-muted-foreground">
              Failed to load chart data
            </div>
          ) : formattedChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={500}>
              {currentConfig.chartType === 'area' ? (
                <AreaChart data={formattedChartData} margin={{ top: 5, right: 30, left: 20, bottom: 80 }}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={currentConfig.color} stopOpacity={0.8} />
                      <stop offset="95%" stopColor={currentConfig.color} stopOpacity={0.2} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="timestamp"
                    style={{ fontSize: '12px' }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis
                    style={{ fontSize: '12px' }}
                    label={{ value: `${currentConfig.label} (${currentConfig.unit})`, angle: -90, position: 'insideLeft' }}
                    width={60}
                    tickCount={8}
                  />
                  <Tooltip
                    formatter={(value: number) => [`${value} ${currentConfig.unit}`, currentConfig.label]}
                  />
                  <Legend verticalAlign="bottom" wrapperStyle={{ paddingTop: '20px' }} />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke={currentConfig.color}
                    fill="url(#colorValue)"
                    name={currentConfig.label}
                    strokeWidth={2}
                  />
                </AreaChart>
              ) : (
                <LineChart data={formattedChartData} margin={{ top: 5, right: 30, left: 20, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="timestamp"
                    style={{ fontSize: '12px' }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis
                    style={{ fontSize: '12px' }}
                    label={{ value: `${currentConfig.label} (${currentConfig.unit})`, angle: -90, position: 'insideLeft' }}
                    width={60}
                    tickCount={8}
                  />
                  <Tooltip
                    formatter={(value: number) => [`${value} ${currentConfig.unit}`, currentConfig.label]}
                  />
                  <Legend verticalAlign="bottom" wrapperStyle={{ paddingTop: '20px' }} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={currentConfig.color}
                    name={currentConfig.label}
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              )}
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[500px] text-muted-foreground">
              No data available for this parameter and time range
            </div>
          )}
        </CardContent>
      </Card>

      {/* Data Table */}
      {formattedChartData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Raw Data</CardTitle>
            <CardDescription>
              {formattedChartData.length} data points
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-h-[400px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-background border-b">
                  <tr>
                    <th className="text-left py-2 px-4 font-medium text-muted-foreground">Timestamp</th>
                    <th className="text-right py-2 px-4 font-medium text-muted-foreground">
                      {currentConfig.label} ({currentConfig.unit})
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {formattedChartData.slice().reverse().map((point, index) => (
                    <tr key={index} className="border-b last:border-b-0">
                      <td className="py-2 px-4">{point.timestamp}</td>
                      <td className="text-right py-2 px-4 font-mono">
                        {typeof point.value === 'number' ? point.value.toFixed(2) : point.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
